import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import moment from "moment";
import VerticalPoints from "../../Common/VerticalPoints/VerticalPoints";
import { formatCurrency } from "../../../Utils";
import { openRightModal } from "../../../REDUX/actions/rightModal";
import UpdateDeparture from "../../Departure/UpdateDeparture";
import {
  DownloadAddress,
  DownloadPackingList,
} from "../../../Utils/deliveryUtils";

const statusLabel = {
  TRANSP: "TRANSP",
  TO_PREP: "TO PREP",
  INCIDENT: "INCIDENT",
  READY: "READY",
  RECUP: "RETRIEVAL",
  DELIVERY: "DELIVERY",
  DELIVERED: "DELIVERED",
  INVOICE: "INVOICED",
};

const statusColors = {
  TRANSP: "#fbb9b9",
  TO_PREP: "#ff8282",
  INCIDENT: "#ff6666",
  READY: "#d79964",
  RECUP: "#42d08b",
  DELIVERY: "#4dae76",
  DELIVERED: "#808080",
  INVOICE: "#808080",
};

export const useDeliveryTable = () => {
  const { t } = useTranslation("common");
  const { user } = useSelector((state) => state.userReducer);
  const dispatch = useDispatch();

  const getOptions = useMemo(() => {
    const userRole = user?.attributes?.userRole;
    if (userRole === 0) {
      return (row) => [
        {
          text: t("Informations"),
          callback: () =>
            dispatch(openRightModal(<UpdateDeparture id={row.id} />)),
        },
        {
          text: t("Download Packing List"),
          callback: () =>
            DownloadPackingList(row?.id, row?.attributes?.number, t),
        },
        {
          text: t("Reception"),
          callback: () =>
            dispatch(
              openRightModal(
                <UpdateDeparture id={row.id} initialTab="Reception" />,
              ),
            ),
        },
        {
          text: t("Exchange"),
          callback: () =>
            dispatch(
              openRightModal(
                <UpdateDeparture id={row.id} initialTab="Exchange" />,
              ),
            ),
        },
      ];
    } else if (userRole === 4) {
      return (row) => [
        {
          text: t("Pickup Date"),
          callback: () =>
            dispatch(
              openRightModal(
                <UpdateDeparture id={row.id} initialTab="Delivery" />,
              ),
            ),
        },
        {
          text: t("Delivery Validation"),
          callback: () =>
            dispatch(
              openRightModal(
                <UpdateDeparture id={row.id} initialTab="Delivery" />,
              ),
            ),
        },
        {
          text: t("Download Address"),
          callback: () => DownloadAddress(row?.id, row?.attributes?.number, t),
        },
        {
          text: t("Download Packing List"),
          callback: () =>
            DownloadPackingList(row?.id, row?.attributes?.number, t),
        },
        {
          text: t("Validate Delivery Price"),
          callback: () =>
            dispatch(
              openRightModal(
                <UpdateDeparture id={row.id} initialTab="Invoice" />,
              ),
            ),
        },
      ];
    }
    return () => [];
  }, [user, t]);

  const headers = useMemo(() => {
    if (user?.attributes?.userRole === 0) {
      return [
        "Date",
        "Order",
        "Type",
        "Ref Client",
        "Transporter",
        "Truck",
        "Delivery date",
        "N°",
        "Status",
        "",
      ];
    } else if (user?.attributes?.userRole === 3) {
      return [
        "Date",
        "Order",
        "Type",
        "Ref Client",
        "Transporter",
        "Truck",
        "Delivery date",
        "N°",
        "Status",
        "",
      ];
    } else if (user?.attributes?.userRole === 4) {
      return [
        "Date",
        "Store/Client",
        "N°",
        "Type",
        "Country",
        "Dpt",
        "PLT",
        "Depot",
        "Recup Date",
        "Price excl. tax",
        "Delivery date",
        "Status",
        "",
      ];
    }
    return [];
  }, [user]);

  const columns = useMemo(() => {
    if (user?.attributes?.userRole === 0) {
      return [
        {
          name: "date",
          render: (row) =>
            row?.attributes?.createdAt
              ? moment(row?.attributes?.createdAt).format("DD/MM/YYYY")
              : "-",
        },
        {
          name: "commande",
          render: (row) =>
            row?.attributes?.commande?.data?.attributes?.number ?? "-",
        },
        {
          name: "type",
          render: (row) =>
            row?.attributes?.commande?.data?.attributes?.packings?.data?.[0]
              ?.attributes?.model?.data?.attributes?.category?.data?.attributes
              ?.name,
        },
        {
          name: "refClient",
          render: (row) =>
            row?.attributes?.commande?.data?.attributes?.refClient ?? "-",
        },
        {
          name: "transporter",
          render: (row) =>
            row?.attributes?.transporter?.data?.attributes?.name ?? "-",
        },
        {
          name: "truck",
          render: (row) => "-",
        },
        {
          name: "deliveryDate",
          render: (row) =>
            row?.attributes?.date
              ? moment(row?.attributes?.date).format("DD/MM/YYYY")
              : "-",
        },
        {
          name: "number",
          render: (row) => row?.attributes?.number ?? "-",
        },
        {
          name: "status",
          render: (row) => (
            <div
              className="py-1 px-2 br14 text-white sBold"
              style={{
                backgroundColor:
                  statusColors[
                    [
                      "RECUP",
                      "DELIVERY",
                      "DELIVERED",
                      "INCIDENT",
                      "INVOICE",
                    ]?.includes(row?.attributes?.deliveryStatus) &&
                    row?.attributes?.status === "READY"
                      ? row?.attributes?.deliveryStatus
                      : row?.attributes?.status
                  ],
              }}
            >
              {t(
                `${
                  statusLabel[
                    [
                      "RECUP",
                      "DELIVERY",
                      "DELIVERED",
                      "INCIDENT",
                      "INVOICE",
                    ]?.includes(row?.attributes?.deliveryStatus) &&
                    row?.attributes?.status === "READY"
                      ? row?.attributes?.deliveryStatus
                      : row?.attributes?.status
                  ]
                }`,
              )}
            </div>
          ),
        },
        {
          name: "options",
          render: (row) => {
            return (
              <VerticalPoints
                showLeft
                className="ms-auto"
                options={getOptions(row)}
              />
            );
          },
        },
      ];
    } else if (user?.attributes?.userRole === 4) {
      return [
        {
          name: "date",
          render: (row) =>
            row?.attributes?.createdAt
              ? moment(row?.attributes?.createdAt).format("DD/MM/YYYY")
              : "-",
        },
        {
          name: "reseller",
          render: (row) =>
            row?.attributes?.commande?.data?.attributes?.reseller?.data
              ?.attributes?.name ?? "-",
        },
        {
          name: "number",
          render: (row) => row?.attributes?.number ?? "-",
        },
        {
          name: "type",
          render: (row) =>
            row?.attributes?.commande?.data?.attributes?.packings?.data?.[0]
              ?.attributes?.model?.data?.attributes?.category?.data?.attributes
              ?.name ?? "-",
        },
        {
          name: "country",
          render: (row) =>
            row?.attributes?.commande?.data?.attributes?.addresseDeLivraison
              ?.data?.attributes?.country?.data?.attributes?.name ?? "-",
        },
        {
          name: "departement",
          render: (row) =>
            row?.attributes?.commande?.data?.attributes?.addresseDeLivraison
              ?.data?.attributes?.city?.data?.attributes?.code ?? "-",
        },
        {
          name: "count",
          render: (row) => {
            const packings =
              row?.attributes?.commande?.data?.attributes?.packings?.data;
            const size = packings?.reduce(
              (acc, packing) =>
                packing?.attributes?.model?.data?.attributes
                  ?.multipleContainer ?? 0 + acc,
              0,
            );

            return size ?? "-";
          },
        },
        {
          name: "depot",
          render: (row) =>
            row?.attributes?.commande?.data?.attributes?.packings?.data[0]
              ?.attributes?.approvisionnement?.data?.attributes?.realEntrepot
              ?.data?.attributes?.name ?? "-",
        },
        {
          name: "recupDate",
          render: (row) =>
            row?.attributes?.recupDate
              ? moment(row?.attributes?.recupDate).format("DD/MM/YYYY")
              : "-",
        },
        {
          name: "deliveryPrice",
          render: (row) =>
            formatCurrency(
              row?.attributes?.commande?.data?.attributes?.totalDeliveryHT || 0,
            ),
        },
        {
          name: "deliveryDate",
          render: (row) =>
            row?.attributes?.commande?.data?.attributes?.date
              ? moment(
                  row?.attributes?.commande?.data?.attributes?.date,
                ).format("DD/MM/YYYY")
              : "-",
        },
        {
          name: "status",
          render: (row) => (
            <div
              className="py-1 px-2 br14 text-white sBold"
              style={{
                backgroundColor:
                  statusColors[
                    [
                      "RECUP",
                      "DELIVERY",
                      "DELIVERED",
                      "INCIDENT",
                      "INVOICE",
                    ]?.includes(row?.attributes?.deliveryStatus) &&
                    row?.attributes?.status === "READY"
                      ? row?.attributes?.deliveryStatus
                      : row?.attributes?.status
                  ],
              }}
            >
              {t(
                `${
                  statusLabel[
                    [
                      "RECUP",
                      "DELIVERY",
                      "DELIVERED",
                      "INCIDENT",
                      "INVOICE",
                    ]?.includes(row?.attributes?.deliveryStatus) &&
                    row?.attributes?.status === "READY"
                      ? row?.attributes?.deliveryStatus
                      : row?.attributes?.status
                  ]
                }`,
              )}
            </div>
          ),
        },
        {
          name: "options",
          render: (row) => {
            return (
              <VerticalPoints
                showLeft
                className="ms-auto"
                options={getOptions(row)}
              />
            );
          },
        },
      ];
    }
  }, [t, user, getOptions]);

  return { headers, columns };
};
